/* eslint-disable react/no-unescaped-entities */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Loader2, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../lib/api';

export default function AgentAIAnalysis({ agentId, agentName }) {
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [expanded, setExpanded] = useState(false);

    const loadAnalysis = async () => {
        if (analysis && !loading) { setOpen(!open); setExpanded(false); return; }
        setOpen(true);
        setExpanded(false);
        setLoading(true);
        try {
            const res = await api.get(`/admin/agency/agent/${agentId}/ai-analysis`);
            setAnalysis(res.data);
        } catch (err) {
            setAnalysis({ analysis: "Erreur lors du chargement de l'analyse.", data: {} });
        } finally {
            setLoading(false);
        }
    };

    const refresh = async () => {
        setLoading(true);
        setExpanded(false);
        try {
            const res = await api.get(`/admin/agency/agent/${agentId}/ai-analysis`);
            setAnalysis(res.data);
        } catch { /* ignore */ } finally { setLoading(false); }
    };

    // Extract a short summary from the AI text (first meaningful paragraph)
    const getSummary = (text) => {
        if (!text) return '';
        const lines = text.split('\n').filter(l => l.trim() && !l.startsWith('**'));
        return lines.slice(0, 2).map(l => l.replace(/\*\*/g, '').replace(/^\*\s+|- /, '').replace(/^\d+\.\s*/, '').trim()).join(' ');
    };

    // Format markdown-like text to JSX
    const formatAnalysis = (text) => {
        if (!text) return null;
        return text.split('\n').map((line, i) => {
            if (line.startsWith('**') && line.endsWith('**')) {
                return <h4 key={i} className="text-xs font-bold text-[#FF914D] mt-2.5 mb-0.5 uppercase tracking-wide">{line.replace(/\*\*/g, '')}</h4>;
            }
            if (line.match(/^\*\*.*\*\*/)) {
                const title = line.match(/\*\*(.*?)\*\*/)?.[1] || '';
                const rest = line.replace(/\*\*.*?\*\*/, '').trim();
                return <div key={i} className="mt-2.5 mb-0.5"><span className="text-xs font-bold text-[#FF914D] uppercase tracking-wide">{title}</span>{rest && <span className="text-[11px] text-muted-foreground"> {rest}</span>}</div>;
            }
            if (line.startsWith('*   ') || line.startsWith('- ')) {
                return <p key={i} className="text-[11px] text-muted-foreground pl-3 py-0.5 border-l-2 border-[#681793]/30 leading-relaxed">{line.replace(/^\*\s+|- /, '').replace(/\*\*/g, '').trim()}</p>;
            }
            if (line.match(/^\d+\./)) {
                return <p key={i} className="text-[11px] text-foreground/80 pl-3 py-0.5 leading-relaxed">{line.replace(/\*\*/g, '').trim()}</p>;
            }
            if (line.trim() === '') return <div key={i} className="h-1.5" />;
            return <p key={i} className="text-[11px] text-muted-foreground leading-relaxed">{line.replace(/\*\*/g, '')}</p>;
        });
    };

    return (
        <div data-testid={`agent-ai-${agentId}`}>
            <Button
                variant="ghost"
                size="sm"
                onClick={loadAnalysis}
                className="h-7 text-xs text-[#681793] hover:text-[#681793]/80 hover:bg-[#681793]/10"
                data-testid={`agent-ai-btn-${agentId}`}
            >
                {loading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                Analyse IA
            </Button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="mt-2 p-4 rounded-xl bg-[#681793]/5 border border-[#681793]/20">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Sparkles className="w-3.5 h-3.5 text-[#FF914D]" />
                                    <span className="text-xs font-bold text-foreground">Analyse IA — {agentName}</span>
                                </div>
                                <Button variant="ghost" size="sm" onClick={refresh} disabled={loading} className="h-6 text-[10px] text-muted-foreground">
                                    <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
                                </Button>
                            </div>
                            {loading ? (
                                <div className="flex items-center gap-2 py-4 justify-center">
                                    <Loader2 className="w-4 h-4 animate-spin text-[#681793]" />
                                    <span className="text-xs text-muted-foreground">Analyse en cours...</span>
                                </div>
                            ) : (
                                <>
                                    {/* Collapsed: short summary */}
                                    {!expanded && (
                                        <div>
                                            <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-3">
                                                {getSummary(analysis?.analysis)}
                                            </p>
                                            <button
                                                onClick={() => setExpanded(true)}
                                                className="mt-2 flex items-center gap-1 text-[11px] font-medium text-[#FF914D] hover:text-[#FF914D]/80 transition-colors"
                                                data-testid={`ai-expand-${agentId}`}
                                            >
                                                Voir l'analyse complète <ChevronDown className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}

                                    {/* Expanded: full analysis */}
                                    {expanded && (
                                        <div>
                                            <div className="space-y-0 max-h-[300px] overflow-y-auto pr-1 scrollbar-thin">
                                                {formatAnalysis(analysis?.analysis)}
                                            </div>
                                            <button
                                                onClick={() => setExpanded(false)}
                                                className="mt-2 flex items-center gap-1 text-[11px] font-medium text-[#FF914D] hover:text-[#FF914D]/80 transition-colors"
                                                data-testid={`ai-collapse-${agentId}`}
                                            >
                                                Réduire <ChevronUp className="w-3 h-3" />
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}

                            {analysis?.data && !loading && (
                                <div className="mt-3 pt-3 border-t border-[#681793]/20 grid grid-cols-4 gap-2">
                                    {[
                                        { label: 'Mandats', value: analysis.data.total_mandates },
                                        { label: 'Vendus', value: analysis.data.sold },
                                        { label: 'Conv.', value: `${analysis.data.conversion_rate}%` },
                                        { label: 'HomlyScore', value: analysis.data.scoretoc }
                                    ].map(({ label, value }) => (
                                        <div key={label} className="text-center">
                                            <p className="text-sm font-bold text-foreground">{value}</p>
                                            <p className="text-[10px] text-muted-foreground">{label}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
